import React, { isValidElement } from 'react';
import isString from 'lodash/isString';
import warning from '../../../lib/_util/warning';
import ObserverCheckBox from '../check-box/CheckBox';
import { FieldType, RecordStatus } from '../data-set/enum';
import ObserverSelect from '../select/Select';
import TreeSelect from '../tree-select/TreeSelect';
import Option from '../option/Option';
import Lov from '../lov/Lov';
import ObserverNumberField from '../number-field/NumberField';
import Currency from '../currency/Currency';
import DatePicker from '../date-picker/DatePicker';
import DateTimePicker from '../date-time-picker/DateTimePicker';
import TimePicker from '../time-picker/TimePicker';
import WeekPicker from '../week-picker/WeekPicker';
import MonthPicker from '../month-picker/MonthPicker';
import YearPicker from '../year-picker/YearPicker';
import ObserverTextField from '../text-field/TextField';
import { ColumnLock, TablePaginationPosition } from './enum';
import IntlField from '../intl-field/IntlField';
import UrlField from '../url-field/UrlField';
import EmailField from '../email-field/EmailField';
import ColorPicker from '../color-picker/ColorPicker';
import Output from '../output/Output';
import Attachment from '../attachment/Attachment';
import { CUSTOMIZED_KEY } from './TableStore';
import { $l } from '../locale-context';
import measureTextWidth from '../_util/measureTextWidth';
import { FuncType } from '../button/enum';
export function getEditorByField(field, isQueryField, isFlat) {
    const lookupCode = field.get('lookupCode');
    const lookupUrl = field.get('lookupUrl');
    const lovCode = field.get('lovCode');
    const multiLine = field.get('multiLine');
    const { type, name } = field;
    const flatProps = isFlat ? { isFlat, maxTagCount: 4, maxTagTextLength: 4 } : {};
    if (lookupCode ||
        isString(lookupUrl) ||
        (type !== FieldType.object && (lovCode || field.lookup || field.get('options')))) {
        if (field.get('parentField')) {
            return React.createElement(TreeSelect, Object.assign({}, flatProps));
        }
        return React.createElement(ObserverSelect, Object.assign({}, flatProps));
    }
    if (lovCode) {
        return React.createElement(Lov, Object.assign({}, flatProps));
    }
    if (multiLine) {
        return React.createElement(Output, null);
    }
    switch (type) {
        case FieldType.boolean:
            return isQueryField ? (React.createElement(ObserverSelect, Object.assign({ clearButton: true }, flatProps),
                React.createElement(Option, { value: true }, $l('Table', 'query_option_yes')),
                React.createElement(Option, { value: false }, $l('Table', 'query_option_no')))) : React.createElement(ObserverCheckBox, null);
        case FieldType.number:
            return React.createElement(ObserverNumberField, Object.assign({}, flatProps));
        case FieldType.currency:
            return React.createElement(Currency, { isFlat: isFlat });
        case FieldType.date:
            return React.createElement(DatePicker, { isFlat: isFlat });
        case FieldType.dateTime:
            return React.createElement(DateTimePicker, { isFlat: isFlat });
        case FieldType.time:
            return React.createElement(TimePicker, { isFlat: isFlat });
        case FieldType.week:
            return React.createElement(WeekPicker, { isFlat: isFlat });
        case FieldType.month:
            return React.createElement(MonthPicker, { isFlat: isFlat });
        case FieldType.year:
            return React.createElement(YearPicker, { isFlat: isFlat });
        case FieldType.intl:
            return React.createElement(IntlField, { isFlat: isFlat });
        case FieldType.email:
            return React.createElement(EmailField, { isFlat: isFlat });
        case FieldType.url:
            return React.createElement(UrlField, { isFlat: isFlat });
        case FieldType.color:
            return React.createElement(ColorPicker, { isFlat: isFlat });
        case FieldType.attachment:
            return React.createElement(Attachment, { viewMode: "popup", funcType: FuncType.link });
        case FieldType.string:
            return React.createElement(ObserverTextField, { isFlat: isFlat });
        default:
            warning(false, `Table auto editor: No editor exists on the field<${name}>'s type<${type}>, so use the TextField as default editor`);
            return React.createElement(ObserverTextField, { isFlat: isFlat });
    }
}
export function getPlaceholderByField(field) {
    if (field) {
        const { type } = field;
        const lookupCode = field.get('lookupCode');
        const lookupUrl = field.get('lookupUrl');
        const lovCode = field.get('lovCode');
        if (lookupCode ||
            isString(lookupUrl) ||
            (type !== FieldType.object && (lovCode || field.lookup || field.get('options')))) {
            return undefined;
        }
        switch (type) {
            case FieldType.number:
            case FieldType.currency:
            case FieldType.string:
            case FieldType.intl:
            case FieldType.email:
            case FieldType.url:
                return $l('Table', 'please_enter');
            default:
        }
    }
}
export function getEditorByColumnAndRecord(column, record) {
    const { name, editor } = column;
    if (record) {
        let cellEditor = editor;
        if (typeof editor === 'function') {
            cellEditor = editor(record, name);
        }
        if (cellEditor === true) {
            const field = record.getField(name);
            if (field) {
                if (!field.get('unique') ||
                    field.get('multiple') ||
                    field.get('range') ||
                    record.status === RecordStatus.add) {
                    return getEditorByField(field);
                }
            }
        }
        if (isValidElement(cellEditor)) {
            return cellEditor;
        }
    }
    if (isValidElement(editor)) {
        return editor;
    }
}
export function isInCellEditor(element) {
    if (element) {
        return !!element.type.__IS_IN_CELL_EDITOR;
    }
    return false;
}
let STICKY_SUPPORT;
export function isStickySupport() {
    if (STICKY_SUPPORT !== undefined) {
        return STICKY_SUPPORT;
    }
    if (typeof window !== 'undefined') {
        const vendorList = ['', '-webkit-', '-ms-', '-moz-', '-o-'];
        const stickyElement = document.createElement('div');
        STICKY_SUPPORT = vendorList.some((vendor) => {
            stickyElement.style.position = `${vendor}sticky`;
            if (stickyElement.style.position !== '') {
                return true;
            }
            return false;
        });
        return STICKY_SUPPORT;
    }
    return true;
}
export function findCell(tableStore, name, lock, record) {
    const { node, dataSet, overflowX, currentEditRecord, prefixCls } = tableStore;
    const current = record || currentEditRecord || dataSet.current;
    if (name !== undefined && current && node.element) {
        const wrapperSelector = !isStickySupport() && overflowX && lock ? `.${prefixCls}-fixed-${lock === true ? ColumnLock.left : lock} ` : '';
        const selector = `${wrapperSelector}tr[data-index="${current.id}"] td[data-index="${name}"] span.${prefixCls}-cell-inner`;
        return node.element.querySelector(selector);
    }
}
export function isCanEdictingRow(element) {
    const sibling = element;
    if (!sibling ||
        ('index' in sibling.dataset &&
            !sibling.getAttributeNodeNS('', 'disabled') &&
            (!document.defaultView || document.defaultView.getComputedStyle(sibling).display !== 'none'))) {
        return true;
    }
    return false;
}
export function findIndexedSibling(element, direction) {
    const sibling = direction > 0 ? element.nextElementSibling : element.previousElementSibling;
    if (isCanEdictingRow(element)) {
        return sibling;
    }
    return findIndexedSibling(sibling, direction);
}
export function isDisabledRow(record) {
    return record.isCached || record.status === RecordStatus.delete;
}
export function isSelectedRow(record) {
    return record.isSelected;
}
export function getHeader(column, dataSet, aggregation) {
    const { header, name, title } = column;
    if (typeof header === 'function') {
        return header(dataSet, name, title, aggregation);
    }
    if (title !== undefined) {
        return title;
    }
    if (header !== undefined) {
        return header;
    }
    const field = dataSet.getField(name);
    if (field) {
        return field.get('label');
    }
}
export function getColumnKey({ name, key }) {
    return key || name;
}
export function getColumnLock(lock) {
    if (lock === true) {
        return ColumnLock.left;
    }
    if (lock) {
        return lock;
    }
    return false;
}
export function getPaginationPosition(pagination) {
    if (pagination) {
        const { position } = pagination;
        if (position) {
            return position;
        }
    }
    return TablePaginationPosition.bottom;
}
export function getHeight(el) {
    return el.getBoundingClientRect().height;
}
export function getTableHeaderRows(columns, currentRow = 0, rows = []) {
    rows[currentRow] = rows[currentRow] || [];
    columns.forEach(column => {
        const { hidden, rowSpan, colSpan, children } = column;
        if (!hidden) {
            if (rowSpan && rows.length < rowSpan) {
                while (rows.length < rowSpan) {
                    rows.push([]);
                }
            }
            if (children) {
                getTableHeaderRows(children.columns, currentRow + rowSpan, rows);
            }
            if (colSpan !== 0) {
                rows[currentRow].push(column);
            }
        }
    });
    return rows;
}
export function isDropresult(dropResult) {
    if (dropResult && dropResult.destination) {
        return ((typeof dropResult.source.index === 'number')
            && (typeof dropResult.destination === 'object')
            && (typeof dropResult.destination.index === 'number'));
    }
    return false;
}
export function isDraggingStyle(style) {
    return style ? 'left' in style : false;
}
export function getMaxClientWidth(element) {
    const { textContent, ownerDocument, clientWidth } = element;
    if (textContent && ownerDocument) {
        const { scrollWidth } = element;
        if (scrollWidth > clientWidth) {
            return scrollWidth;
        }
        const { defaultView } = ownerDocument;
        if (defaultView) {
            const computedStyle = defaultView.getComputedStyle(element);
            const { paddingLeft, paddingRight } = computedStyle;
            const pl = paddingLeft ? parseFloat(paddingLeft) : 0;
            const pr = paddingRight ? parseFloat(paddingRight) : 0;
            if (pl || pr) {
                const textWidth = measureTextWidth(textContent, computedStyle) + pl + pr;
                if (textWidth > clientWidth) {
                    return textWidth;
                }
            }
        }
    }
    return clientWidth;
}
export function onlyCustomizedColumn(tableStore) {
    const { rightLeafs } = tableStore.columnGroups;
    return rightLeafs.length === 0 || rightLeafs[0].key === CUSTOMIZED_KEY;
}
